import { useDispatch, useSelector } from 'react-redux';

import { updateSmsAddon } from 'data/reducers/subscriptionReducer';
import { openModal } from 'data/reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import {
	SMS_ADDON_MONTHLY_PRICE,
	SMS_ADDON_PURCHASE_CONSENT_MESSAGE,
	SMS_ADDON_PURCHASE_CONFIRM_LABEL,
	getSmsAddonState,
} from './smsAddon';

/**
 * SMS Instant Alerts add-on, in the v3 Settings layout. Same purchase and cancel
 * flow as the add-on section of SubscriptionCard: it renders only when the
 * backend reports the add-on flag, and buying requires an active subscription.
 */
const SmsAddonCard = () => {
	const dispatch = useDispatch();
	const { currentSubscription, isUpdatingSmsAddon } = useSelector(
		(state) => state.subscription || {}
	);

	const { backendReportsSmsAddon, hasSmsAddon } =
		getSmsAddonState(currentSubscription);
	if (!currentSubscription?.id || !backendReportsSmsAddon) {
		return null;
	}

	const subscription = currentSubscription?.subscription || currentSubscription;
	const subscriptionStatus = subscription?.stripe_status || 'active';

	const handleAdd = () =>
		dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				title: 'Add SMS Instant Alerts?',
				message: SMS_ADDON_PURCHASE_CONSENT_MESSAGE,
				confirmLabel: SMS_ADDON_PURCHASE_CONFIRM_LABEL,
				closeLabel: 'Cancel',
				color: 'success',
				onConfirm: () => dispatch(updateSmsAddon(true)),
			})
		);

	const handleCancel = () =>
		dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				title: 'Cancel SMS Alerts Add-on?',
				message:
					'Are you sure you want to cancel the SMS Instant Alerts add-on? You will no longer receive real-time SMS notifications.',
				confirmLabel: 'Yes, Cancel Add-on',
				closeLabel: 'Keep Add-on',
				color: 'danger',
				onConfirm: () => dispatch(updateSmsAddon(false)),
			})
		);

	return (
		<div
			className={`tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 tw-rounded-[8px] tw-border tw-p-5 ${
				hasSmsAddon
					? 'tw-border-[#33C581] tw-bg-[#F2FDF9]'
					: 'tw-border-[#D9D9D9] tw-bg-[#F7F7F7]'
			}`}
		>
			<div className="tw-min-w-0">
				<div className="tw-flex tw-items-center tw-gap-2.5 tw-mb-1">
					<h3 className="!tw-ml-0 tw-text-base tw-font-bold tw-text-text-primary tw-m-0">
						SMS Instant Alerts
					</h3>
					{hasSmsAddon && (
						<span className="tw-inline-flex tw-items-center tw-bg-[#C9F9EA] tw-text-[#161616] tw-border tw-border-[#33C581] tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium">
							Active
						</span>
					)}
				</div>
				<p className="tw-text-sm tw-text-text-secondary tw-m-0">
					{`Instant real-time SMS for Stop Work Orders, ECB/DOB hearings and critical deadlines (${SMS_ADDON_MONTHLY_PRICE}/month recurring).`}
				</p>
			</div>
			<div className="tw-flex-shrink-0">
				{hasSmsAddon ? (
					<button
						type="button"
						className="tw-bg-[#FFBDC9] tw-text-[#161616] tw-border tw-border-[#FE8AA1] hover:tw-bg-[#FFAAB9] tw-rounded-[4px] tw-px-3.5 tw-py-1.5 tw-text-sm tw-font-medium disabled:tw-opacity-50"
						disabled={isUpdatingSmsAddon}
						onClick={handleCancel}
					>
						{isUpdatingSmsAddon ? 'Updating...' : 'Cancel Add-on'}
					</button>
				) : (
					<button
						type="button"
						className="tw-bg-[#63F2C5] tw-text-[#161616] tw-border tw-border-[#33C581] hover:tw-bg-[#4EE6B5] tw-rounded-[4px] tw-px-3.5 tw-py-1.5 tw-text-sm tw-font-medium disabled:tw-opacity-50"
						disabled={isUpdatingSmsAddon || subscriptionStatus !== 'active'}
						onClick={handleAdd}
						title={
							subscriptionStatus !== 'active'
								? 'Available with an active subscription'
								: undefined
						}
					>
						{isUpdatingSmsAddon
							? 'Activating...'
							: `Add (+${SMS_ADDON_MONTHLY_PRICE}/mo)`}
					</button>
				)}
			</div>
		</div>
	);
};

export default SmsAddonCard;
